import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Progress } from './ui/progress';
import { TauriAPI } from '../lib/tauri';
import { CheckCircle, Loader2, Monitor } from 'lucide-react';

interface ScanProgress {
  step: string;
  percentage: number;
  message: string;
}

interface SystemScanProgressProps {
  onScanComplete: (result: any) => void;
  startScan: boolean;
}

export const SystemScanProgress: React.FC<SystemScanProgressProps> = ({
  onScanComplete,
  startScan,
}) => {
  const [progress, setProgress] = useState<ScanProgress>({
    step: 'initializing',
    percentage: 0,
    message: 'Preparing system scan...',
  });
  const [isScanning, setIsScanning] = useState(false);

  useEffect(() => {
    if (!startScan || isScanning) return;

    const performScan = async () => {
      setIsScanning(true);
      
      // Listen for progress updates
      const unlistenProgress = await TauriAPI.onProgress(setProgress);

      try {
        // Start the scan
        setProgress({ step: 'starting', percentage: 5, message: 'Starting system scan...' });
        
        const result = await TauriAPI.scanSystem();
        
        setProgress({ 
          step: 'complete', 
          percentage: 100, 
          message: 'System scan completed successfully!' 
        });
        
        setTimeout(() => {
          onScanComplete(result);
        }, 1000);
      } catch (error) {
        console.error('System scan failed:', error);
        setProgress({
          step: 'error',
          percentage: 0,
          message: `Scan failed: ${error}`,
        });
      } finally {
        unlistenProgress();
        setIsScanning(false);
      }
    };

    performScan();
  }, [startScan, isScanning, onScanComplete]);

  const getStepIcon = () => {
    if (progress.step === 'complete') {
      return <CheckCircle className="w-6 h-6 text-green-500" />;
    }
    if (progress.step === 'error') {
      return <Monitor className="w-6 h-6 text-red-500" />;
    }
    return <Loader2 className="w-6 h-6 animate-spin text-blue-500" />;
  };

  const getStepDescription = (step: string) => {
    const stepDescriptions: Record<string, string> = {
      initializing: 'Preparing system scan...',
      starting: 'Starting system analysis...',
      scanning_apps: 'Discovering installed applications...',
      scanning_dev_tools: 'Finding development tools...',
      scanning_claude: 'Looking for Claude installations...',
      analyzing_integrations: 'Analyzing potential integrations...',
      complete: 'System scan completed successfully!',
      error: 'An error occurred during scanning',
    };
    return stepDescriptions[step] || progress.message;
  };

  return (
    <div className="max-w-2xl mx-auto">
      <Card>
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            {getStepIcon()}
          </div>
          <CardTitle>System Scan in Progress</CardTitle>
          <CardDescription>
            Analyzing your Mac system to configure ChittyOS
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="font-medium">
                {getStepDescription(progress.step)}
              </span>
              <span className="text-muted-foreground">
                {Math.round(progress.percentage)}%
              </span>
            </div>
            <Progress value={progress.percentage} className="h-2" />
          </div>

          <div className="space-y-2 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-blue-500" />
              <span>Scanning installed applications</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-blue-500" />
              <span>Detecting development tools</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-blue-500" />
              <span>Finding Claude installations</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-blue-500" />
              <span>Analyzing integration opportunities</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};